import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { CheckMarkIcon, CreditCardIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import TextInput from '../../../../../../packages/ui/components/atoms/input/text-input'
import ArrowRightOneIcon from '../../../../../../packages/ui/icons/arrow-right-01'
import { getCseByCode } from '../../../api/cse/get-cse-by-code'
import { getPaymentRecordsStatusesStatistics } from '../../../api/enrollments/get-payment-records-statistics'
import { payBalance } from '../../../api/enrollments/pay-balance'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ClientSpacePageTitle from '../../../components/pages/client-space/client-space-page-title'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { BillingTypeEnum } from '../../../lib/types/enums/cse/billing-type.enum'
import { PaymentStatusEnum } from '../../../lib/types/enums/enrollment/payment-status.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const PaymentOption = ({ label }: { label: string }) => (
  <div className="flex items-start gap-2">
    <div className="bg-secondaryRed-light p-1 rounded-full">
      <ArrowRightOneIcon height="13" width="13" color={tailwindColors.secondaryRed.DEFAULT} />
    </div>
    <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
      {label}
    </Typography>
  </div>
)

const ClientFinalizePaymentPage = () => {
  const [value, setValue] = useState<string>('')
  const { enrollment } = useEnrollment()

  const { data: paymentRecordsStatistics } = useQuery({
    queryKey: ['paymentRecordsStatistics', enrollment.id],
    queryFn: () => getPaymentRecordsStatusesStatistics({ enrollmentId: enrollment.id })
  })

  const { data: cse } = useQuery({
    queryKey: ['cse', enrollment.id],
    queryFn: () => {
      const cseCode = enrollment.cseRegistration?.cse?.code
      if (!cseCode) throw new Error('CSE code is missing')
      return getCseByCode(cseCode, 'BASIC')
    },
    enabled: !!enrollment.cseRegistration?.cse?.code
  })

  const { mutate: payBalanceMutation, isPending: isPaymentLoading } = useMutation({
    mutationFn: () => payBalance(enrollment.id, parseFloat(value)),
    onError: showAxiosError
  })

  const isPaid = enrollment.paymentStatus === PaymentStatusEnum.FULLY_PAID
  const isCancelled = EnrollmentProperties.isCancelled(enrollment)
  const remainingBalance = paymentRecordsStatistics?.pendingAmount ?? 0

  useEffect(() => {
    setValue(remainingBalance.toString())
  }, [remainingBalance])

  if (cse && cse.billingType === BillingTypeEnum.FCSE)
    return <ItemNotFound title="Vous ne pouvez pas accéder à cette page" />
  if (!paymentRecordsStatistics) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-4">
      <ClientSpacePageTitle
        icon={<CreditCardIcon color={tailwindColors.primary.dark.DEFAULT} />}
        title="Régler le solde du séjour"
      />
      {isPaid ? (
        <div className="flex flex-col items-center justify-center gap-4 w-full ">
          <Badge
            className="w-full"
            size="medium"
            iconPosition="start"
            icon={
              <div className="flex items-center justify-center w-5 h-5  rounded-full border border-green-light-active">
                <CheckMarkIcon color={tailwindColors.green.DEFAULT} />
              </div>
            }
            variant="success"
            text="Merci, le solde du séjour a été réglé."
          />
        </div>
      ) : (
        <>
          <InnerBox className="rounded-xl p-4 ">
            <InnerBox className="rounded-lg py-3 px-4 bg-white-hover flex flex-col gap-3">
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                Pour procéder au règlement, vous pouvez choisir l´une des trois options suivantes :
              </Typography>
              <PaymentOption label="Procéder par carte bancaire ou virement à totalité du montant du solde en une fois" />
              <PaymentOption
                label="Procéder par carte bancaire ou virement à un règlement en plusieurs fois. Si vous choisissez cette option, 
            vous pourrez alors définir le montant des différents versements que vous souhaitez effectuer. 
          Vous pouvez aussi utiliser plusieurs cartes bancaires différentes (par exemple, en cas de problème de plafond)"
              />
            </InnerBox>
            <div className="h-4" />
            <Typography variant="tag" color={tailwindColors.grey.lighter}>
              Nota : Dans le cadre du paiement sécurisé en ligne, aucune des informations (nom du
              titulaire, numéro complet de votre carte, date de validité, etc.) n'est collectée,
              conservée ou même connue de LEC. Ces informations sont communiquées directement et
              uniquement à LCL (Le Crédit Lyonnais) par l'intermédiaire des serveurs sécurisés de
              cette banque.
            </Typography>
          </InnerBox>
          <InnerBox className="rounded-lg py-3 px-4 bg-white-hover flex flex-row w-full justify-between items-center mobile:flex-col mobile:items-start">
            <InnerBox className="bg-white">
              <div className="flex items-center gap-3 mobile:flex-col">
                <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
                  {paymentRecordsStatistics.balanceDueDate
                    ? `Solde restant dû, à régler avant le ${DateFormatter.DDMMYYYY(paymentRecordsStatistics.balanceDueDate)} : ${remainingBalance}€`
                    : `Solde restant dû : ${remainingBalance}€`}
                </Typography>
                <InnerBox className="border-primary-light-active bg-primary-light flex flex-row items-center gap-1 p-2 mobile:w-full ">
                  <Typography variant="tag" color={tailwindColors.primary.dark.DEFAULT}>
                    Je souhaite régler :
                  </Typography>
                  <TextInput
                    className="w-16 mobile:w-20 pr-4 pl-1"
                    type="number"
                    value={value}
                    allowNegative={false}
                    disabled={isCancelled}
                    onChange={(e) => {
                      setValue(e.target.value)
                    }}
                  />
                </InnerBox>
              </div>
            </InnerBox>
            <div className="flex items-center gap-2 mobile:flex-col mobile:w-full mobile:mt-3">
              {isCancelled && (
                <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
                  Le séjour est annulé, aucun paiement n'est dû.
                </Typography>
              )}
              <Button
                label="Payer par carte bancaire"
                icon={CreditCardIcon}
                disabled={isCancelled || !value || parseFloat(value) <= 0 || isPaymentLoading}
                variant="primary"
                iconColor={tailwindColors.white.DEFAULT}
                className="w-full text-nowrap"
                onClick={() => payBalanceMutation()}
                loading={isPaymentLoading}
              />
              {/*
              //todo: uncomment when virement feature is available
              <Button
                label="Payer par virement"
                icon={BankIcon}
                disabled={!value || parseFloat(value) <= 0}
                variant="primary"
                className="w-full"
              /> */}
            </div>
          </InnerBox>
        </>
      )}
    </div>
  )
}

export default ClientFinalizePaymentPage
